import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';
import { Award, Truck, ShieldCheck, Heart, Users, Clock, CheckCircle, ArrowRight, Star } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const WhyChooseUsPage = () => {
  usePageTitle('Why Choose Us', 'Discover why crafters trust Stitch Your Dash for quality sewing tools, fast shipping, and excellent customer service.');

  const reasons = [
    {
      icon: Award,
      title: 'Quality Products',
      description: 'We carefully curate our selection to include only high-quality tools and supplies that meet our strict standards.'
    },
    {
      icon: Truck,
      title: 'Fast USA Shipping',
      description: 'Orders ship quickly via USPS. Standard shipping takes 5-7 business days, with free shipping on orders over $99.'
    },
    {
      icon: ShieldCheck,
      title: 'Secure Shopping',
      description: 'Shop with confidence. Our checkout is protected with SSL 256-bit encryption to keep your information safe.'
    },
    {
      icon: Heart,
      title: '30-Day Returns',
      description: 'Not satisfied? Return unused items within 30 days for a full refund. No questions asked.'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Our friendly customer service team is available to help with any questions or concerns.'
    },
    {
      icon: Clock,
      title: 'Same-Day Processing',
      description: 'Orders placed before 3 PM EST are processed the same day, getting your tools to you faster.'
    }
  ];

  const stats = [
    { number: '11+', label: 'Quality Products' },
    { number: '$99', label: 'Free Shipping' },
    { number: '30', label: 'Day Returns' },
    { number: '5-7', label: 'Days Delivery' }
  ];

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Hero with Banner Image */}
        <section className="relative bg-warmBrown-800 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1578353022142-09264fd64295?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-900 via-warmBrown-900/90 to-warmBrown-800/70" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-20" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-2xl">
              <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-6">
                <Star className="w-8 h-8 text-rose-400" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4">Why Choose {BRAND_INFO.name}?</h1>
              <p className="text-warmBrown-300 text-lg">We're committed to providing crafters with the best tools, service, and shopping experience.</p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-rose-500 py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="font-display text-4xl text-white font-bold">{stat.number}</p>
                  <p className="text-rose-100 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reasons Grid */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl text-warmBrown-900">What Sets Us Apart</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reasons.map((reason, index) => (
                <div key={index} className="bg-white rounded-3xl p-8 shadow-card border border-cream-200 hover:shadow-card-hover transition-shadow">
                  <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mb-6">
                    <reason.icon className="w-7 h-7 text-rose-500" />
                  </div>
                  <h3 className="font-display text-xl text-warmBrown-900 mb-3">{reason.title}</h3>
                  <p className="text-warmBrown-600 leading-relaxed">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Promise */}
        <section className="py-16 lg:py-20 bg-cream-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-card border border-cream-200 text-center">
              <CheckCircle className="w-12 h-12 text-rose-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-warmBrown-900 mb-6">Our Promise to You</h2>
              <p className="text-warmBrown-600 text-lg leading-relaxed mb-8">
                At {BRAND_INFO.name}, we stand behind every product we sell. If you're not completely satisfied with your purchase, we'll make it right. That's our promise to you.
              </p>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20 group"
              >
                Start Shopping
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WhyChooseUsPage;
